import React from 'react';
import { IBannerModel } from '@shared/models/alerts/IBannerModel';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { subscribe } from '../helpers/EventCallback';

interface AlertsState {
    // State
    bannerMap: Record<string, IBannerModel>;
    bannersArray: IBannerModel[];
    error: string | null;
    success: string | null;
    warning: string | null;

    // Actions
    addBanner: (newBanner: IBannerModel, duration?: number) => void;
    removeBanner: (id: string) => void;
    showError: (error: string, timeout?: number) => void;
    showSuccess: (success: string, timeout?: number) => void;
    showWarning: (warning: string, timeout?: number) => void;
}

export const useAlertsStore = create<AlertsState>()(
    immer((set, get) => {
        const showError = (error: string, timeout = 5000) => {
            set((state) => {
                state.error = error;
            });
            setTimeout(() => {
                set((state) => {
                    state.error = null;
                });
            }, timeout);
        };

        const showSuccess = (success: string, timeout = 5000) => {
            set((state) => {
                state.success = success;
            });
            setTimeout(() => {
                set((state) => {
                    state.success = null;
                });
            }, timeout);
        };

        const showWarning = (warning: string, timeout = 5000) => {
            set((state) => {
                state.warning = warning;
            });
            setTimeout(() => {
                set((state) => {
                    state.warning = null;
                });
            }, timeout);
        };

        const addBanner = (newBanner: IBannerModel, duration?: number) => {
            const existing = get().bannerMap[newBanner.id];
            if (existing?.timeout) clearTimeout(existing.timeout);
            let timeout: NodeJS.Timeout | undefined;
            if (duration !== undefined) {
                timeout = setTimeout(() => {
                    get().removeBanner(newBanner.id);
                }, duration);
            }
            set((state) => {
                state.bannerMap[newBanner.id] = { ...newBanner, timeout };
                state.bannersArray = Object.values(state.bannerMap);
            });
        };

        const removeBanner = (id: string) => {
            const banner = get().bannerMap[id];
            if (banner?.timeout) clearTimeout(banner.timeout);
            set((state) => {
                delete state.bannerMap[id];
                state.bannersArray = Object.values(state.bannerMap);
            });
        };

        return {
            // State
            bannerMap: {},
            bannersArray: [],
            error: null,
            success: null,
            warning: null,

            // Actions
            addBanner,
            removeBanner,
            showError,
            showSuccess,
            showWarning,
        };
    })
);

subscribe('alertSuccess', (alert) => {
    useAlertsStore.getState().showSuccess(alert.message);
});
subscribe('alertError', (alert) => {
    useAlertsStore.getState().showError(alert.message);
});
subscribe('alertWarning', (alert) => {
    useAlertsStore.getState().showWarning(alert.message);
});
